import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MainButtonComponent } from '../../shared/ui/main-button/main-button.component';
import { Person } from '../../api/person-api.class';
import { AsyncPipe, Location, NgClass, NgForOf, NgIf } from '@angular/common';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import {
  FormBuilder,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import {
  Firma,
  Spindeigenschaften,
  UpdatePersonInput,
} from '../../api/person-api.types';
import { PersonApiService } from '../../api/person-api.service';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  firstValueFrom,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  throwError,
} from 'rxjs';
import { FormInputComponent } from '../../shared/ui/form-input/form-input.component';
import { FormSelectComponent } from '../../shared/ui/form-select/form-select.component';
import { FormTextareaComponent } from '../../shared/ui/form-textarea/form-textarea.component';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { LockerApiService } from '../../api/locker-api.service';
import { UpdateLockerInput } from '../../api/locker-api.types';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { DateTime } from 'luxon';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { StoreService } from '../../store/store.service';
import { GetClothingOutput } from '../../api/clothing-api.types';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { ScannerService } from '../../shared/services/scanner.service';
import {
  CreateProvisionRequestInput,
  GetMatchcodesOutput,
} from '../../api/provision-request-api.types';
import { ClothingApiService } from '../../api/clothing-api.service';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { ProvisionRequestApiService } from '../../api/provision-request-api.service';
import { Locker } from '../../api/locker-api.class';

@Component({
  selector: 'app-person-detail',
  standalone: true,
  imports: [
    NzIconModule,
    MainButtonComponent,
    NgIf,
    NzSpinComponent,
    NgClass,
    ReactiveFormsModule,
    NzModalModule,
    FormInputComponent,
    FormSelectComponent,
    FormTextareaComponent,
    NzAlertModule,
    NzDropDownModule,
    NgForOf,
    AsyncPipe,
    NzInputDirective,
    FormsModule,
    NzTooltipDirective,
    NzAutocompleteModule,
    NzDatePickerModule,
    NzInputGroupComponent,
  ],
  templateUrl: './person-detail.component.html',
  styleUrl: './person-detail.component.scss',
})
export class PersonDetailComponent implements OnInit, OnDestroy {
  person$: Observable<Person | null>;
  personClothing$: Observable<GetClothingOutput[]>;
  personLockers$: Observable<Spindeigenschaften[]>;
  clothingSizes: GetMatchcodesOutput[] = [];
  companies: Firma[];
  id!: number;
  editMode = false;
  avsPerson = true;
  selectOptionsGender = [
    { value: 1, label: 'Männlich' },
    { value: 2, label: 'Weiblich' },
    { value: 3, label: 'Divers' },
  ];
  scannedCodeBuffer = '';
  scanTimeout: any;
  scanSpeedTreshold = 50;
  scanStartTime = 0;
  private routeSub: any;
  private boundKeyDownHandler = this.onKeyDown.bind(this);

  // Locker assignment properties
  private searchSubject = new Subject<string>();
  searchedLockers$?: Observable<Locker[]>;
  isLoading = false;
  modalIsVisible = false;
  searchTerm?: string;
  date: Date[] = [];

  validatePersonForm = this.fb.group({
    optionalFields: this.fb.group({
      firmaId: this.fb.control<number | null>(null),
      bemerkung: this.fb.control<string | null>(null),
      groesse_hemd: this.fb.control<string | null>(null),
      groesse_hose: this.fb.control<string | null>(null),
      groesse_jacke: this.fb.control<string | null>(null),
      groesse_kittel: this.fb.control<string | null>(null),
      groesse_schuh: this.fb.control<string | null>(null),
    }),
    mandatoryFields: this.nonNullableFb.group({
      geschlecht: this.nonNullableFb.control(0, [Validators.required]),
      vorname: this.nonNullableFb.control('', [Validators.required]),
      name: this.nonNullableFb.control('', [Validators.required]),
      gruppe: this.nonNullableFb.control('', [Validators.required]),
    }),
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private nonNullableFb: NonNullableFormBuilder,
    private modal: NzModalService,
    private store: StoreService,
    private personApiService: PersonApiService,
    private lockerApiService: LockerApiService,
    private scannerService: ScannerService,
    private clothingApiService: ClothingApiService,
    private provisionApiService: ProvisionRequestApiService,
    private message: NzMessageService,
    private location: Location,
  ) {
    this.person$ = this.store.select((state) => state.persons.activePerson);
    this.companies = this.route.snapshot.data['companies'] ?? [];
    this.personClothing$ = this.store.select(
      (state) => state.persons.activePersonClothing,
    );
    this.personLockers$ = this.store.select(
      (state) => state.persons.activePersonLockers,
    );
  }

  async ngOnInit() {
    window.addEventListener('keydown', this.boundKeyDownHandler);

    this.clothingSizes = await this.clothingApiService.getClothingSizes();
    const person = await firstValueFrom(this.person$);
    if (person) {
      await this.prefillForm(person);
    }
    this.routeSub = this.router.events.subscribe(async (event) => {
      if (event instanceof NavigationEnd) {
        const person = await firstValueFrom(this.person$);
        if (person) {
          this.avsPerson = person.typ !== 5;
          await this.prefillForm(person);
        }
      }
    });

    // Setup locker search
    this.searchedLockers$ = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter((searchTerm) => searchTerm.length >= 1),
      switchMap((searchTerm) => {
        this.isLoading = true;
        return this.lockerApiService
          .searchLocker({ bezeichnung: searchTerm, status: 1 })
          .pipe(
            map((response) => Locker.fromResultArray(response)),
            catchError((err) => {
              if (err.status === 404) {
                return of([]);
              } else {
                return throwError(() => err);
              }
            }),
            finalize(() => {
              this.isLoading = false;
            }),
          );
      }),
    );
  }

  ngOnDestroy() {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
    window.removeEventListener('keydown', this.boundKeyDownHandler);
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key.length > 1) {
      return;
    }
    const currentTime = DateTime.now().toMillis();
    if (this.scannedCodeBuffer === '') {
      this.scanStartTime = currentTime;
    }

    this.scannedCodeBuffer += event.key;

    clearTimeout(this.scanTimeout);
    this.scanTimeout = setTimeout(async () => {
      const scannedCode = this.scannedCodeBuffer.trim();
      this.scannedCodeBuffer = '';

      if (scannedCode) {
        const scanDuration = DateTime.now().toMillis() - this.scanStartTime;
        const avgCharTime = scanDuration / scannedCode.length;

        if (avgCharTime < this.scanSpeedTreshold) {
          await this.onScanSuccess(scannedCode);
        }
      }
    }, 100);
  }

  async prefillForm(person: Person) {
    this.validatePersonForm.get('mandatoryFields')?.patchValue({
      name: person.name,
      vorname: person.vorname,
      geschlecht: person.rawGeschlecht,
      gruppe: person.gruppe ?? 'Keine Gruppe',
    });
    if (this.companies) {
      const personCompany = this.companies.find(
        (company) => company.oid === person.firma.oid,
      );

      this.validatePersonForm.get('optionalFields')?.patchValue({
        firmaId: personCompany?.oid ?? null,
        bemerkung: person.bemerkung ?? null,
        groesse_hemd: person.groesseHemd,
        groesse_hose: person.groesseHose,
        groesse_jacke: person.groesseJacke,
        groesse_kittel: person.groesseKittel,
        groesse_schuh: person.groesseSchuhe,
      });
    }
  }

  navigateBack() {
    // this.router.navigate(['persons'])
    this.location.back();
  }

  switchToEditMode() {
    this.editMode = !this.editMode;
  }

  savePerson() {
    this.modal.confirm({
      nzTitle: 'Möchten Sie die Änderungen speichern?',
      nzContent:
        '<b style="color: red;">Klicken Sie auf "Nein" um das Bearbeiten abzubrechen.</b>',
      nzOkText: 'Ja',
      nzOkType: 'primary',
      nzOkDanger: false,
      nzOnOk: async () => {
        this.editMode = !this.editMode;
        const person = await firstValueFrom(this.person$);
        if (person) {
          await this.updatePerson(person.id);
        }
      },
      nzCancelText: 'Nein',
      nzOnCancel: () => {
        this.editMode = !this.editMode;
      },
    });
  }

  async updatePerson(personId: number) {
    if (this.validatePersonForm.valid) {
      const mandatoryValues = this.validatePersonForm
        .get('mandatoryFields')
        ?.getRawValue();
      const optionalValues =
        this.validatePersonForm.get('optionalFields')?.value;

      const person: UpdatePersonInput = {
        id: personId,
        ...mandatoryValues,
        ...optionalValues,
      };
      const activePerson = await firstValueFrom(this.person$);
      const personType = activePerson?.typ ?? 1;
      try {
        const result = await this.personApiService.updatePerson(
          personId,
          personType,
          person,
        );
        if (result) {
          this.message.success('Person erfolgreich aktualisiert!');
        }
      } catch (error) {
        this.message.error('Die Person konnte nicht aktualisiert werden.');
        console.log(error);
      }
    }
  }

  isReadOnly(alwaysReadOnly: boolean): boolean {
    return this.avsPerson || alwaysReadOnly || !this.editMode;
  }

  cancel(): void {
    this.editMode = !this.editMode;
  }

  onReleaseLockerClick(locker: Spindeigenschaften) {
    this.modal.confirm({
      nzTitle: '<i>Schlüsselrückgabe</i>',
      nzContent: '<b>Möchten Sie diesen Spind wirklich freigeben?</b>',
      nzOkText: 'Ja',
      nzCancelText: 'Nein',
      nzOkType: 'primary',
      nzOnOk: () => this.releaseLocker(locker),
    });
  }

  async releaseLocker(spindEigenschaften: Spindeigenschaften) {
    const person = await firstValueFrom(this.person$);
    if (person) {
      const locker = await this.lockerApiService.getLocker(spindEigenschaften.spindId);
      await this.confirmDeleteBemerkung(locker);
      await this.lockerApiService.withdrawLockerKey(spindEigenschaften.spindId, {
        ruecknahmedatum: DateTime.now().toISODate(),
      });
      await this.lockerApiService.releaseLocker(spindEigenschaften.spindId);
      const {
        persons: { setActivePerson },
      } = this.store.getActions();
      await setActivePerson(person.id);
    }

    this.message.success('Spind erfolgreich freigegeben.');
  }

  async onWithdrawSecondKeyClick(locker: Spindeigenschaften) {
    const person = await firstValueFrom(this.person$);

    if (person) {
      try {
        await this.lockerApiService.withdrawLockerKey(locker.spindId, {
          ruecknahmedatum: DateTime.now().toISODate(),
        });
        const {
          persons: { setActivePerson },
        } = this.store.getActions();
        await setActivePerson(person.id);

        this.message.success('Zweitschlüssel erfolgreich zurückgenommen.');
      } catch {
        this.message.error(
          'Etwas bei der Rücknahme des Zweitschlüssels ist schiefgelaufen.',
        );
      }
    }
  }

  async onScanSuccess(result: string) {
    //TODO Fallunterscheidung ob Personenkarte
    const isClothing = !result.startsWith('P-');
    const person = await firstValueFrom(this.person$);
    if (person) {
      if (isClothing) {
        await this.scannerService.scanClothing(result, person.id);
        const {
          persons: { setActivePerson },
        } = this.store.getActions();
        await setActivePerson(person.id);
        const activePerson = await firstValueFrom(this.person$);
        if (activePerson) {
          await this.prefillForm(activePerson);
        }
      } else {
        await this.scannerService.scanPerson(result);
      }
    }
  }

  onAssignClick() {
    this.modalIsVisible = true;
  }

  onInput() {
    if (this.searchTerm) this.searchSubject.next(this.searchTerm);
  }

  handleCancel() {
    this.modalIsVisible = false;
    this.searchTerm = '';
    this.date = [];
  }

  async handleOk() {
    const person = await firstValueFrom(this.person$);
    if (!person) return;

    const lockerId = parseInt(this.searchTerm ? this.searchTerm : '');

    if (isNaN(lockerId)) {
      this.message.warning('Bitte einen vorgeschlagenen Spind auswählen');
      return;
    }

    const locker = await this.lockerApiService.getLocker(lockerId);
    // Check if person already has a locker of type 1, 2, or 3
    const alreadyHasMainLocker = person.spinde.some(
      (locker) =>
        locker.spindartId === 1 ||
        locker.spindartId === 2 ||
        locker.spindartId === 3,
    );

    const isMainLocker =
      locker.rawSpindart === 1 ||
      locker.rawSpindart === 2 ||
      locker.rawSpindart === 3;

    const triesToAddDupMainLocker = alreadyHasMainLocker && isMainLocker;

    if (triesToAddDupMainLocker) {
      this.message.warning(
        'Dieser Person ist bereits ein Hauptspind (schmal, breit, doppelt) zugewiesen. ' +
          'Bitte geben Sie diesen erst frei.',
        { nzDuration: 5000 },
      );
      return;
    }

    // Check if person already has a locker with the same type as the new locker
    const duplicateLocker = person.spinde.find(
      (dLocker) => dLocker.spindartId === locker.rawSpindart,
    );

    if (duplicateLocker) {
      this.message.warning(
        `Spinde der Kategorie ${locker.spindart} können nicht mehrfach zugewiesen werden.`,
      );
      return;
    }

    // Check if the selected start date is in the past
    if (!this.date[0] || !this.date[1]) {
      this.message.warning('Bitte wählen Sie einen Zeitraum aus.');
      return;
    }

    if (
      DateTime.fromJSDate(this.date[0]).startOf('day') <
      DateTime.now().startOf('day')
    ) {
      this.message.warning(
        'Das Startdatum darf nicht in der Vergangenheit liegen.',
      );
      return;
    }

    const personName = `${person.vorname} ${person.name}`;
    const {
      lockers: { assignLocker },
    } = this.store.getActions();

    const requestInput: CreateProvisionRequestInput = {
      bezeichnung: `Einzelausgabe Spind ${personName}`,
      datum_von: this.date[0].toISOString(),
      datum_bis: this.date[1].toISOString(),
      modulId: 0,
      kleidungsart: 'Keine Angabe',
      spindart: locker.getLockerOutput.spindart,
      bemerkung: `Einzelausgabe Spind, PersonenId: ${person.id}`,
      spindstandort: locker.getLockerOutput.standort,
      personen: [person.id],
    };

    await this.provisionApiService.createProvisionRequest(requestInput);
    await this.confirmDeleteBemerkung(locker);
    await assignLocker(lockerId, { spindpersonId: person.id });

    const {
      persons: { setActivePerson },
    } = this.store.getActions();
    await setActivePerson(person.id);

    this.modalIsVisible = false;
    this.searchTerm = '';
    this.date = [];
  }

  async onHandoutSecondKey(locker: Spindeigenschaften) {
    const person = await firstValueFrom(this.person$);

    if (person) {
      try {
        await this.lockerApiService.handoutLockerKey(locker.spindId, {
          ausgabedatum: DateTime.now().toISODate(),
        });

        const {
          persons: { setActivePerson },
        } = this.store.getActions();
        await setActivePerson(person.id);
        this.message.success(
          'Ein zweiter Schlüssel wurde erfolgreich ausgegeben.',
        );
      } catch {
        this.message.error(
          'Es kann kein weiterer Schlüssel mehr ausgeben werden.',
        );
      }
    }
  }

  private async confirmDeleteBemerkung(locker: Locker) {
    if (!locker.bemerkung || locker.bemerkung.trim().length === 0) return;

    const confirmed = await new Promise<boolean>((resolve) => {
      this.modal.confirm({
        nzTitle: 'Bemerkung löschen?',
        nzContent: `<b>Der Spind enthält aktuell eine Bemerkung:</b><br><i>"${locker.bemerkung}"</i><br><br>Möchten Sie diese löschen?`,
        nzOkText: 'Ja, löschen',
        nzCancelText: 'Nein, behalten',
        nzOnOk: () => resolve(true),
        nzOnCancel: () => resolve(false),
      });
    });

    if (confirmed) {
      const updateLocker: UpdateLockerInput = {
        bezeichnung: locker.bezeichnung,
        standort: locker.getLockerOutput.standort,
        geschlecht: locker.getLockerOutput.geschlecht,
        spindart: locker.getLockerOutput.spindart,
        status: locker.getLockerOutput.status,
        bemerkung: '',
      };
      await this.lockerApiService.updateLocker(locker.id, updateLocker);
      this.message.info('Die Bemerkung wurde gelöscht.');
    }
  }
}
